import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { 
  Settings, 
  MapPin, 
  Check, 
  X, 
  Save, 
  ToggleLeft, 
  ToggleRight,
  Loader2,
  Map,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Users
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { ticketService } from '../../services/api';
import { STADIUMS, findStadium } from '../../data/stadiums';
import StadiumPlanModal from '../tickets/StadiumPlanModal';

// Standard categories
const STANDARD_CATEGORIES = [
  { value: '1', label: 'CATEGORIE 1', color: 'bg-yellow-500' },
  { value: '2', label: 'CATEGORIE 2', color: 'bg-red-500' },
  { value: '3', label: 'CATEGORIE 3', color: 'bg-blue-500' },
  { value: '4', label: 'CATEGORIE 4', color: 'bg-green-500' },
];

export const StadiumMappingView = ({ tickets = [], project = '' }) => {
  const [mappings, setMappings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedStadiums, setExpandedStadiums] = useState(new Set());
  const [stadiumPlanOpen, setStadiumPlanOpen] = useState(false);
  const [selectedStadium, setSelectedStadium] = useState(null);
  
  // Load mappings from backend
  useEffect(() => {
    loadMappings();
  }, []);
  
  const loadMappings = async () => {
    setLoading(true);
    try {
      const data = await ticketService.getStadiumMappings();
      setMappings(data || {});
    } catch (error) {
      console.error('Error loading mappings:', error);
      toast.error('Erreur lors du chargement des mappings');
    } finally {
      setLoading(false);
    }
  };
  
  // Get all unique non-standard categories from VENTE tickets per stadium
  const stadiumCategories = React.useMemo(() => {
    const result = {};
    
    // Only VENTE tickets
    const venteTickets = tickets.filter(t => t.type === 'VENTE');
    
    venteTickets.forEach(ticket => {
      const stadium = findStadium(ticket.lieu, ticket.ville, ticket.evenement, project);
      if (!stadium) return;
      
      const category = ticket.categorie?.toUpperCase()?.trim();
      if (!category) return;
      
      // Check if it's a non-standard category (doesn't match CATEGORIE X pattern)
      const isStandard = /^(CATEGORIE|CATEGORY|CAT)\s*[1-4]$/i.test(category);
      
      if (!result[stadium.id]) {
        result[stadium.id] = {
          stadium,
          categories: new Set(),
          standardCategories: new Set(),
          totalTickets: 0
        };
      }
      
      result[stadium.id].totalTickets++;
      
      if (isStandard) {
        result[stadium.id].standardCategories.add(category);
      } else {
        result[stadium.id].categories.add(category);
      }
    });
    
    // Convert Sets to Arrays
    Object.keys(result).forEach(key => {
      result[key].categories = Array.from(result[key].categories).sort();
      result[key].standardCategories = Array.from(result[key].standardCategories).sort();
    });
    
    return result;
  }, [tickets]);
  
  // Toggle stadium expansion
  const toggleStadium = (stadiumId) => {
    setExpandedStadiums(prev => {
      const next = new Set(prev);
      if (next.has(stadiumId)) {
        next.delete(stadiumId);
      } else {
        next.add(stadiumId);
      }
      return next;
    });
  };
  
  // Update mapping for a category - now supports multi-select
  const updateMapping = (stadiumId, category, targetCategory) => {
    setMappings(prev => {
      const currentCategories = prev[stadiumId]?.categories?.[category] || [];
      // Convert to array if it's a single value (backwards compatibility)
      const currentArray = Array.isArray(currentCategories) ? currentCategories : (currentCategories ? [currentCategories] : []);
      
      let newCategories;
      if (targetCategory === null) {
        // Clear all
        newCategories = [];
      } else if (currentArray.includes(targetCategory)) {
        // Remove if already selected
        newCategories = currentArray.filter(c => c !== targetCategory);
      } else {
        // Add to selection
        newCategories = [...currentArray, targetCategory];
      }
      
      return {
        ...prev,
        [stadiumId]: {
          ...prev[stadiumId],
          enabled: prev[stadiumId]?.enabled ?? true,
          categories: {
            ...prev[stadiumId]?.categories,
            [category]: newCategories.length > 0 ? newCategories : null
          }
        }
      };
    });
  };
  
  // Toggle mapping enabled/disabled for a stadium
  const toggleMappingEnabled = (stadiumId) => {
    setMappings(prev => ({
      ...prev,
      [stadiumId]: {
        ...prev[stadiumId],
        enabled: !(prev[stadiumId]?.enabled ?? false),
        categories: prev[stadiumId]?.categories || {}
      }
    }));
  };
  
  // Save all mappings
  const saveMappings = async () => {
    setSaving(true);
    try {
      await ticketService.saveStadiumMappings(mappings);
      toast.success('Mappings sauvegardés !');
    } catch (error) {
      console.error('Error saving mappings:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  
  // Show stadium plan
  const showPlan = (stadium, e) => {
    e?.stopPropagation();
    setSelectedStadium(stadium);
    setStadiumPlanOpen(true);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-[#C4A052]" />
        <span className="ml-3 text-zinc-400">Chargement des mappings...</span>
      </div>
    );
  }
  
  const stadiumsWithCategories = Object.entries(stadiumCategories).filter(
    ([_, data]) => data.categories.length > 0
  );
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Settings className="h-5 w-5 text-[#C4A052]" />
            Configuration Mapping Stades
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            Configurez les correspondances de catégories pour l'attribution automatique
          </p>
        </div>
        <Button
          onClick={saveMappings}
          disabled={saving}
          className="bg-[#C4A052] hover:bg-[#D4B86A] text-black"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Sauvegarder
        </Button>
      </div>
      
      {/* Info */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-sm">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-blue-400 shrink-0 mt-0.5" />
          <div className="text-blue-300">
            <strong>Comment ça marche :</strong>
            <ul className="mt-1 space-y-1 text-blue-300/80">
              <li>• Les catégories non standard (UPPER, SIDELINE, etc.) apparaissent ici</li>
              <li>• Mappez chaque catégorie vers une catégorie standard (1, 2, 3 ou 4)</li>
              <li>• Activez le mapping pour permettre l'attribution automatique</li>
              <li>• Sans mapping actif = correspondance exacte uniquement</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Stadiums List */}
      {stadiumsWithCategories.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Aucune catégorie non standard trouvée</p>
          <p className="text-sm mt-1">Les ventes avec des catégories comme UPPER, SIDELINE, etc. apparaîtront ici</p>
        </div>
      ) : (
        <div className="space-y-3">
          {stadiumsWithCategories.map(([stadiumId, data]) => {
            const isExpanded = expandedStadiums.has(stadiumId);
            const stadiumMapping = mappings[stadiumId] || { enabled: false, categories: {} };
            const isEnabled = stadiumMapping.enabled;
            // Count mapped categories (those with at least one selection)
            const mappedCount = Object.values(stadiumMapping.categories || {}).filter(v => 
              v && (Array.isArray(v) ? v.length > 0 : true)
            ).length;
            
            return (
              <div 
                key={stadiumId}
                className={cn(
                  "border rounded-lg overflow-hidden transition-colors",
                  isEnabled 
                    ? "border-emerald-500/50 bg-emerald-500/5" 
                    : "border-zinc-700 bg-zinc-800/30"
                )}
              >
                {/* Stadium Header */}
                <div 
                  onClick={() => toggleStadium(stadiumId)}
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-800/50"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-zinc-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-zinc-400" />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{data.stadium.name}</span>
                        <span className="text-xs text-zinc-500">({data.stadium.city})</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-zinc-400 mt-1">
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {data.stadium.capacity.toLocaleString()} places
                        </span>
                        <span className="text-amber-400">
                          {data.categories.length} catégorie(s) à mapper
                        </span>
                        {mappedCount > 0 && (
                          <span className="text-emerald-400">
                            {mappedCount} mappée(s)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => showPlan(data.stadium, e)}
                      className="border-[#C4A052]/50 text-[#C4A052] hover:bg-[#C4A052]/10"
                    >
                      <Map className="h-4 w-4 mr-1" />
                      Plan
                    </Button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMappingEnabled(stadiumId);
                      }}
                      className={cn(
                        "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors",
                        isEnabled
                          ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/50"
                          : "bg-zinc-700 text-zinc-400 border border-zinc-600"
                      )}
                    >
                      {isEnabled ? (
                        <>
                          <ToggleRight className="h-4 w-4" />
                          Actif
                        </>
                      ) : (
                        <>
                          <ToggleLeft className="h-4 w-4" />
                          Inactif
                        </>
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Expanded Content - Categories Mapping */}
                {isExpanded && (
                  <div className="border-t border-zinc-700 p-4 space-y-3">
                    <div className="text-xs text-zinc-500 mb-2">
                      Sélectionnez la catégorie standard correspondante pour chaque catégorie vendue :
                    </div>
                    
                    {data.categories.map((category) => {
                      const currentMapping = stadiumMapping.categories?.[category];
                      // Convert to array for multi-select support
                      const selectedCategories = Array.isArray(currentMapping) 
                        ? currentMapping 
                        : (currentMapping ? [currentMapping] : []);
                      
                      return (
                        <div 
                          key={category}
                          className="flex items-center justify-between bg-zinc-800 rounded-lg p-3"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-white bg-zinc-700 px-2 py-1 rounded">
                              {category}
                            </span>
                            <span className="text-zinc-500">→</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {STANDARD_CATEGORIES.map((cat) => {
                              const isSelected = selectedCategories.includes(cat.value);
                              return (
                                <button
                                  key={cat.value}
                                  onClick={() => updateMapping(stadiumId, category, cat.value)}
                                  className={cn(
                                    "flex items-center gap-1 px-3 py-1.5 rounded text-sm font-medium transition-all",
                                    isSelected
                                      ? `${cat.color} text-white ring-2 ring-white/30`
                                      : "bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
                                  )}
                                >
                                  {isSelected && <Check className="h-3 w-3" />}
                                  CAT {cat.value}
                                </button>
                              );
                            })}
                            
                            {selectedCategories.length > 0 && (
                              <button
                                onClick={() => updateMapping(stadiumId, category, null)}
                                className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"
                                title="Supprimer tous les mappings"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Show standard categories found */}
                    {data.standardCategories.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-zinc-700">
                        <div className="text-xs text-zinc-500 mb-2">
                          Catégories standard déjà présentes (pas besoin de mapping) :
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {data.standardCategories.map((cat) => (
                            <span 
                              key={cat}
                              className="text-xs bg-zinc-700 text-zinc-300 px-2 py-1 rounded"
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Stadium Plan Modal */}
      <StadiumPlanModal
        open={stadiumPlanOpen}
        onOpenChange={setStadiumPlanOpen}
        stadium={selectedStadium}
      />
    </div>
  );
};

export default StadiumMappingView;
